import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAnalytics } from '@vaptmenu/analytics-react';
import {
  getProfile,
  updateProfile,
  getStoreSettings,
  updateStoreSettings,
  getDeliverySettings,
  updateDeliverySettings,
  getPaymentMethods,
  updatePaymentMethods,
  uploadFile,
  getMenuDomain,
  updateMenuDomain,
  checkSlug,
} from '../api/settings.api';
import type {
  TenantProfile,
  UpdateProfileRequest,
  StoreSettings,
  UpdateStoreSettingsRequest,
  DeliverySettings,
  UpdateDeliverySettingsRequest,
  PaymentMethodsResponse,
  UpdatePaymentMethodsRequest,
  FileUploadResponse,
  MenuDomainResponse,
  UpdateMenuDomainRequest,
  CheckSlugResponse,
} from '../types/settings.types';
import type { ApiError } from '@/shared/lib/problem-details';
import { useAuthStore } from '@/features/auth/stores/auth-store';

/* ==========================================================================
   Query Keys — scoped by tenant
   ========================================================================== */

export const settingsKeys = {
  root: (tenantId: string) => ['settings', tenantId] as const,
  profile: (tenantId: string) => [...settingsKeys.root(tenantId), 'profile'] as const,
  store: (tenantId: string) => [...settingsKeys.root(tenantId), 'store'] as const,
  delivery: (tenantId: string) => [...settingsKeys.root(tenantId), 'delivery'] as const,
  paymentMethods: (tenantId: string) =>
    [...settingsKeys.root(tenantId), 'payment-methods'] as const,
  menuDomain: (tenantId: string) =>
    [...settingsKeys.root(tenantId), 'menu-domain'] as const,
  /** Sobre e observação de pagamento (change store-and-catalog-knowledge). */
  storeKnowledge: (tenantId: string) =>
    [...settingsKeys.root(tenantId), 'store-knowledge'] as const,
  storeFaq: (tenantId: string) => [...settingsKeys.root(tenantId), 'store-faq'] as const,
  /** A pausa, os feriados e a situação de funcionamento agora. */
  storeClosures: (tenantId: string) =>
    [...settingsKeys.root(tenantId), 'store-closures'] as const,
  checkSlug: (tenantId: string, slug: string, excludeTenantId?: string) =>
    [
      ...settingsKeys.root(tenantId),
      'check-slug',
      slug,
      excludeTenantId ?? '',
    ] as const,
};

function settingsTenantId(): string {
  return useAuthStore.getState().selectedTenant?.id ?? '';
}

/* ==========================================================================
   Profile
   ========================================================================== */

export function useProfile() {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<TenantProfile, ApiError>({
    queryKey: settingsKeys.profile(tenantId),
    queryFn: getProfile,
    enabled: !!tenantId,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  return useMutation<TenantProfile, ApiError, UpdateProfileRequest>({
    mutationFn: updateProfile,
    onSuccess: (data) => {
      track('panel.settings_saved', { section: 'profile' });
      const tid = settingsTenantId();
      if (tid) {
        queryClient.setQueryData(settingsKeys.profile(tid), data);
      }
    },
  });
}

/* ==========================================================================
   Store Settings
   ========================================================================== */

export function useStoreSettings() {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<StoreSettings, ApiError>({
    queryKey: settingsKeys.store(tenantId),
    queryFn: getStoreSettings,
    enabled: !!tenantId,
  });
}

export function useUpdateStoreSettings() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  return useMutation<StoreSettings, ApiError, UpdateStoreSettingsRequest>({
    mutationFn: updateStoreSettings,
    onSuccess: (data) => {
      track('panel.settings_saved', { section: 'store' });
      const tid = settingsTenantId();
      if (tid) {
        queryClient.setQueryData(settingsKeys.store(tid), data);
        // A mesma consulta alimenta o indicador "Aceite automático ligado" da
        // tela de pedidos: invalidar faz quem a observa reler, sem recarregar.
        queryClient.invalidateQueries({ queryKey: settingsKeys.store(tid) });
        // Horário e fuso mudam a situação de funcionamento do cartão "Pausa e
        // feriados", que vem da leitura dos fechamentos.
        queryClient.invalidateQueries({ queryKey: settingsKeys.storeClosures(tid) });
      }
    },
  });
}

/* ==========================================================================
   Delivery Settings
   ========================================================================== */

export function useDeliverySettings() {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<DeliverySettings, ApiError>({
    queryKey: settingsKeys.delivery(tenantId),
    queryFn: getDeliverySettings,
    enabled: !!tenantId,
  });
}

export function useUpdateDeliverySettings() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  return useMutation<DeliverySettings, ApiError, UpdateDeliverySettingsRequest>({
    mutationFn: updateDeliverySettings,
    onSuccess: (data) => {
      track('panel.settings_saved', { section: 'delivery' });
      const tid = settingsTenantId();
      if (tid) {
        queryClient.setQueryData(settingsKeys.delivery(tid), data);
      }
    },
  });
}

/* ==========================================================================
   Payment Methods
   ========================================================================== */

export function usePaymentMethods() {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<PaymentMethodsResponse, ApiError>({
    queryKey: settingsKeys.paymentMethods(tenantId),
    queryFn: getPaymentMethods,
    enabled: !!tenantId,
  });
}

export function useUpdatePaymentMethods() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  return useMutation<PaymentMethodsResponse, ApiError, UpdatePaymentMethodsRequest>({
    mutationFn: updatePaymentMethods,
    onSuccess: (data) => {
      track('panel.settings_saved', { section: 'payment' });
      const tid = settingsTenantId();
      if (tid) {
        queryClient.setQueryData(settingsKeys.paymentMethods(tid), data);
      }
    },
  });
}

/* ==========================================================================
   Menu Domain
   ========================================================================== */

export function useMenuDomain() {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<MenuDomainResponse, ApiError>({
    queryKey: settingsKeys.menuDomain(tenantId),
    queryFn: getMenuDomain,
    enabled: !!tenantId,
  });
}

export function useUpdateMenuDomain() {
  const queryClient = useQueryClient();

  return useMutation<MenuDomainResponse, ApiError, UpdateMenuDomainRequest>({
    mutationFn: updateMenuDomain,
    onSuccess: (data) => {
      const tid = settingsTenantId();
      if (tid) {
        queryClient.setQueryData(settingsKeys.menuDomain(tid), data);
        queryClient.invalidateQueries({ queryKey: settingsKeys.profile(tid) });
      }
    },
  });
}

export function useCheckSlug(slug: string, excludeTenantId?: string) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<CheckSlugResponse, ApiError>({
    queryKey: settingsKeys.checkSlug(tenantId, slug, excludeTenantId),
    queryFn: () => checkSlug(slug, excludeTenantId),
    enabled: !!tenantId && slug.length >= 3,
    staleTime: 10_000,
  });
}

/* ==========================================================================
   File Upload
   ========================================================================== */

export function useUploadFile() {
  return useMutation<FileUploadResponse, ApiError, { file: File; context: 'logo' | 'product' }>({
    mutationFn: ({ file, context }) => uploadFile(file, context),
  });
}
